import { useEffect, useState } from 'react';
import circleBg from '@/assets/notifications/market-closing-soon/medallion-circle-bg.svg';
import bell from '@/assets/notifications/market-closing-soon/medallion-bell.png';
import { POPUP_CTA } from '@/lib/notifications/popup-cta';
import { marketTitle, metaNumber, metaString } from '@/lib/notifications/meta';
import { Medallion } from '../Medallion';
import { PopupCta } from '../PopupCta';
import type { PopupViewProps } from '../types';

const CTA = POPUP_CTA['market-closing-soon'];

/** Format whole seconds as mm:ss. */
function mmss(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/**
 * Resolve the countdown target (ms epoch), computed once on mount. Prefers the
 * absolute `meta.closeTime` (ISO); otherwise anchors `meta.secondsRemaining` to
 * mount time. Returns undefined when neither is present. `Date.now()` is only
 * touched inside the lazy `useState` initializer (runs once, off the render path).
 */
function useCloseTarget(meta: PopupViewProps['notification']['meta']): number | undefined {
  const [target] = useState<number | undefined>(() => {
    const closeTime = metaString(meta, 'closeTime');
    if (closeTime) {
      const t = new Date(closeTime).getTime();
      if (!Number.isNaN(t)) return t;
    }
    const secs = metaNumber(meta, 'secondsRemaining');
    return secs !== undefined ? Date.now() + secs * 1000 : undefined;
  });
  return target;
}

/**
 * MARKET_CLOSING_SOON — warning tone. Peach circle + orange bell medallion, a
 * live mm:ss countdown in warning orange (`--warning` #ff8000), and a "Trade
 * Now" CTA (still brand purple) deep-linking to the market. Reference build for
 * the "warning-tone + live countdown" pattern.
 */
export function MarketClosingSoonPopup({ notification, onAction }: PopupViewProps) {
  const meta = notification.meta;
  const title = marketTitle(meta);
  const target = useCloseTarget(meta);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (target === undefined) return;
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      // The countdown clamps at 00:00 (mmss floors at 0) — stop ticking once we
      // reach the target instead of spinning a timer forever.
      if (t >= target) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [target]);

  const remaining = target !== undefined ? mmss((target - now) / 1000) : undefined;

  return (
    <>
      <Medallion circleSrc={circleBg} illustrationSrc={bell} tone="warning" alt="" />

      <div className="flex flex-col items-center gap-2">
        <h2 className="text-3xl font-semibold tracking-[-0.6px] text-ink">Market Closing Soon</h2>

        {title && <p className="text-xl font-bold text-ink">{`“${title}”`}</p>}

        {remaining && (
          <p className="text-[46px] font-bold leading-none tracking-[-0.92px] text-warning" data-testid="closing-countdown">
            {remaining}
          </p>
        )}
        <p className="text-xl text-ink">minutes left to trade</p>
      </div>

      <PopupCta
        label={CTA.primary.label}
        width={234}
        onClick={() => onAction(CTA.primary.action)}
        testId="popup-cta-primary"
      />
    </>
  );
}
